import { Controller } from "@hotwired/stimulus";
import { showNotice } from "../services/notice_service";
import ConfirmationModalService from "../services/confirmation_modal_service";
import { ImageModalService } from "../services/image_modal_service";

export default class extends Controller {
  static targets = [
    "addBtn",
    "addAllBtn",
    "tbody",
    "variantSelect",
    "quantityInput",
    "removeBtn",
    "moqMessages",
    "thumbnail",
    "thumbnailCarouselBtns",
    "copyBtn",
    "summaryBtn",
  ];

  connect() {
    if (!this.hasTbodyTarget) return;

    this.params = JSON.parse(this.data.get("params"));

    this.rebuild();

    this.reset();
  }

  // Re-renders the table from the current this.params.lines. Used both on
  // initial connect and to restore the UI after a failed apply().
  rebuild() {
    this.tbodyTarget.replaceChildren();

    this.params.lines.forEach((line) => {
      this.add(line);
    });

    this.checkButtons();
  }

  checkButtons() {
    if (this.hasAddBtnTarget && this.hasAddAllBtnTarget) {
      this.addBtnTarget.disabled = this.addAllBtnTarget.disabled =
        this.params.lines.length == this.params.variants_for_select.length;

      this.removeBtnTargets.forEach(
        (btn) => (btn.disabled = this.removeBtnTargets.length == 1),
      );

      this.removeBtnTargets.forEach(
        (btn) => (btn.disabled = this.removeBtnTargets.length == 1),
      );
    }

    if (this.params.stock) {
      const distinctVariants = this.getDistinctSelectedVariants();

      if (
        !distinctVariants.some(
          (dv) => this.thumbnailTarget.src == dv.thumbnail.url,
        )
      ) {
        this.thumbnailTarget.src = distinctVariants[0].thumbnail.url;
      }

      this.thumbnailCarouselBtnsTarget.style.display =
        distinctVariants.length > 1 ? "" : "none";
    }
  }

  // Snapshots the current lines as the last known-good, server-confirmed state.
  reset() {
    this.currentLines = $.extend(true, [], this.params.lines);
  }

  getUnselectedVariants(currentVariantId) {
    const selectedVariants = this.params.lines
      .map((l) => l.product_variant_id)
      .filter((l) => l != null);

    return this.params.variants_for_select.filter((v) => {
      return (
        !selectedVariants.some((sv) => v[1] == sv) || currentVariantId == v[1]
      );
    });
  }

  getDistinctSelectedVariants() {
    return Array.from(
      new Map(
        this.params.variants
          .filter((v) =>
            this.params.lines.some((l) => l.product_variant_id == v.id),
          )
          .map((v) => [v.sku, v]),
      ).values(),
    );
  }

  updateSelectOptions(select, val) {
    select.options.length = 0;

    this.getUnselectedVariants(val).forEach((v) => {
      let opt = document.createElement("option");
      opt.value = v[1];
      opt.innerHTML = v[0];
      select.append(opt);
    });

    if (![...select.options].some(opt => opt.value == val)) {
      let opt = document.createElement("option");
      opt.value = val;
      opt.innerHTML = this.params.lines.find(l => l.product_variant_id == val)?.product_variant_name;
      opt.disabled = true;
      select.append(opt);
    }

    select.value = val;
  }

  addClick() {
    const line = {
      product_variant_id: this.getUnselectedVariants()[0][1],
      quantity: this.params.moq_rule?.line_min_qty ?? 1,
    };

    this.params.lines.push(line);
    this.add(line);

    this.checkButtons();
    this.apply();
  }

  addAllClick() {
    this.getUnselectedVariants().forEach((variant) => {
      const line = {
        product_variant_id: variant[1],
        quantity: this.params.moq_rule?.line_min_qty ?? 1,
      };

      this.params.lines.push(line);
      this.add(line);
    });

    this.checkButtons();
    this.apply();
  }

  apply = $.debounce((callback) => {
    this.applyCallback = callback;
    this.applyQueued = true;
    this.flushApply();
  }, 1000);

  // One save at a time per product. A slow update used to overlap the next one,
  // and both inserts were stored.
  flushApply() {
    if (this.applyInFlight || !this.applyQueued) return;

    this.applyQueued = false;
    this.applyInFlight = true;
    const callback = this.applyCallback;
    this.applyCallback = null;

    const request = $.ajax(this.params.url, {
      method: "PUT",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
      contentType: "application/json",
      dataType: "json",
      data: JSON.stringify({ lines: this.params.lines }),
    });

    request
      .then((result) => {
        // Request succeeded - the current lines are now the confirmed state.
        this.reset();

        $("#checkoutBtn").prop("disabled", !result.can_checkout);
        this.dispatch("updateMoq", {
          detail: result,
        });
        if (result.order_line_data) {
          this.updateButtonUrls(result.order_line_data);
        }
      })
      .catch(() => {
        if (this.applyQueued) return;

        // Request failed - the optimistic UI change (row removed, quantity
        // edited, variant swapped, etc.) never actually persisted server-side.
        // Roll back to the last confirmed state instead of leaving the UI
        // showing something the backend doesn't have.
        this.params.lines = $.extend(true, [], this.currentLines);
        this.rebuild();

        showNotice("Something went wrong");
      });

    request.always(() => {
      this.applyInFlight = false;
      callback?.();
      this.flushApply();
    });
  }

  updateButtonUrls(orderLineData) {
    if (this.hasCopyBtnTarget) {
      const copyBtn = this.copyBtnTarget;
      const orderId = copyBtn.dataset.orderId;
      if (orderId) {
        const newPosition = orderLineData.max_position + 1;
        const newUrl = `/orders/${orderId}/add_products_to_gallery?order_line_id=${orderLineData.id}&position=${newPosition}`;
        copyBtn.href = newUrl;
        copyBtn.dataset.orderLineId = orderLineData.id;
      }
    }
    if (this.hasSummaryBtnTarget) {
      const summaryBtn = this.summaryBtnTarget;
      const orderId = summaryBtn.dataset.orderId;
      if (orderId && orderLineData.revision_recipe_id) {
        const newUrl = `/orders/recipe_summary_modal?recipe_id=${orderLineData.revision_recipe_id}&id=${orderId}&order_line_id=${orderLineData.id}&source=cart`;
        summaryBtn.href = newUrl;
        summaryBtn.dataset.orderLineId = orderLineData.id;
        summaryBtn.dataset.revisionRecipeId = orderLineData.revision_recipe_id;
        const productActive = summaryBtn.closest('.card').querySelector('[data-cart-item-params]');
        if (productActive) {
          try {
            const params = JSON.parse(productActive.dataset.cartItemParams);
            const shouldBeDisabled = !orderLineData.revision_recipe_id || !params.product_active;
            if (shouldBeDisabled) {
              summaryBtn.classList.add('disabled');
              summaryBtn.style.pointerEvents = 'none';
            } else {
              summaryBtn.classList.remove('disabled');
              summaryBtn.style.pointerEvents = '';
            }
          } catch (e) {
          }
        }
      } else if (orderId && !orderLineData.revision_recipe_id) {
        summaryBtn.classList.add('disabled');
        summaryBtn.style.pointerEvents = 'none';
      }
    }
  }

  updateMoq({ detail: errors_info }) {
    this.moqMessagesTarget.replaceChildren();
    errors_info?.messages?.[this.params.group_id]?.forEach((message) => {
      $("<li>").text(message).appendTo(this.moqMessagesTarget);
    });
  }

  add(line) {
    const tr = document.createElement("tr");
    let td = document.createElement("td");
    td.setAttribute("valign", "middle");

    const rosterInfo = document.createElement("div");
    const rosterQuantity = document.createElement("span");
    const quantityText = document.createElement("span");
    const rosterBtn = document.createElement("a");

    const variantSelect = document.createElement("select");
    variantSelect.dataset.cartItemTarget = "variantSelect";
    variantSelect.classList.add("form-select");
    variantSelect.style.minWidth = "100px";
    variantSelect.disabled = !this.params.product_active || this.params.only_quantity_mode;

    this.updateSelectOptions(variantSelect, line.product_variant_id);

    const refreshRosterBtn = () => {
      const variant = this.params.variants.find(
        (v) => v.id == line.product_variant_id,
      );
      const url = new URL(
        this.params.manage_roster_path,
        window.location.origin,
      );
      url.searchParams.append("product_variant_id", line.product_variant_id);
      rosterBtn.href = url;

      if (this.params.roster_mode && variant.has_custom_number) {
        rosterInfo.style.display = rosterBtn.style.display = "";
      } else {
        rosterInfo.style.display = rosterBtn.style.display = "none";
      }
    };

    variantSelect.addEventListener("change", () => {
      line.product_variant_id = +variantSelect.value;
      refreshRosterBtn();

      this.variantSelectTargets.forEach((vs) =>
        this.updateSelectOptions(vs, vs.value),
      );

      this.checkButtons();
      rosterBtn.classList.add("disabled");
      this.apply(() => rosterBtn.classList.remove("disabled"));
    });

    td.append(variantSelect);
    tr.append(td);

    td = document.createElement("td");
    td.setAttribute("valign", "middle");
    const quantityInput = document.createElement("input");
    const rule = this.params.moq_rule;
    const lineIncrQty = rule?.line_incr_qty ?? 1;
    let effectiveMin = rule?.line_min_qty ?? 0;
    if (
      this.params.is_cs &&
      this.params.is_stick &&
      (this.params.order_type === "WARRANTY" || this.params.is_stick_2x)
    ) {
      effectiveMin = 1;
    }
    quantityInput.setAttribute("type", "number");
    quantityInput.step = lineIncrQty < 1 ? 1 : lineIncrQty;
    quantityInput.min = effectiveMin;
    quantityInput.max = rule?.line_max_qty || "";
    quantityInput.classList.add("form-control");
    quantityInput.value = line.quantity;
    quantityInput.dataset.cartItemTarget = "quantityInput";
    quantityInput.disabled = !this.params.product_active;

    const refreshQuantity = () => {
      line.quantity = +quantityInput.value;
      quantityText.innerText = line.quantity;
      this.checkButtons();
    };

    const applyChanges = () => {
      if (
        effectiveMin != null &&
        +quantityInput.value < effectiveMin
      ) {
        quantityInput.value = effectiveMin;
      }

      if (
        rule?.line_max_qty != null &&
        +quantityInput.value > rule.line_max_qty
      ) {
        quantityInput.value = rule.line_max_qty;
      }

      if (rule?.line_incr_qty > 1) {
        var result =
          Math.ceil(+quantityInput.value / rule.line_incr_qty) *
          rule.line_incr_qty;
        quantityInput.value = result ? result : quantityInput.min;
      }

      const currentValue = line.quantity;

      refreshQuantity();

      if (currentValue != line.quantity) {
        let deferred = $.Deferred().resolve();

        if (this.params.roster_mode) {
          const variant = this.params.variants.find(
            (v) => v.id == line.product_variant_id,
          );

          if (variant.has_custom_number) {
            const rosterQuantity = line.roster?.length ?? 0;

            if (line.quantity < rosterQuantity) {
              deferred = ConfirmationModalService.show(
                "This QTY will remove custom numbers, are you sure you wish to continue?",
              );
            }
          }
        }

        deferred
          .then(() => {
            rosterBtn.classList.add("disabled");
            this.apply(() => {
              rosterBtn.classList.remove("disabled");
              line.roster = line.roster?.slice(0, line.quantity);
              rosterQuantity.innerText = line.roster?.length ?? 0;
            });
          })
          .catch(() => {
            quantityInput.value = currentValue;
            refreshQuantity();
          });
      }
    };

    quantityInput.addEventListener("click", applyChanges);

    quantityInput.addEventListener("focusout", applyChanges);

    quantityInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        applyChanges();
      }
    });

    quantityInput.addEventListener("input", () => {
      if (
        effectiveMin != null &&
        +quantityInput.value < effectiveMin
      ) {
        quantityInput.value = effectiveMin;
      }
    });

    td.append(quantityInput);
    tr.append(td);

    td = document.createElement("td");
    td.style.width = "1%";

    td.setAttribute("align", "center");
    td.setAttribute("valign", "middle");

    const container = document.createElement("div");
    container.classList.add("d-flex", "align-items-center");

    if (this.params.product_active && !this.params.only_quantity_mode) {
      const removeBtn = document.createElement("button");
      removeBtn.classList.add("btn", "btn-primary", "btn-sm");
      removeBtn.dataset.cartItemTarget = "removeBtn";

      const removeIcon = document.createElement("i");
      removeIcon.classList.add("bi-trash");

      removeBtn.append(removeIcon);

      removeBtn.addEventListener("click", () => {
        tr.remove();
        this.params.lines.splice(this.params.lines.indexOf(line), 1);

        this.variantSelectTargets.forEach((vs) =>
          this.updateSelectOptions(vs, vs.value),
        );

        this.checkButtons();
        this.apply();
      });

      container.append(removeBtn);
    }

    if (this.params.roster_mode) {
      rosterInfo.classList.add("me-3");

      const title = document.createElement("div");
      title.innerText = "#s";
      rosterInfo.append(title);

      const value = document.createElement("div");
      rosterInfo.append(value);

      rosterQuantity.innerText = line.roster?.length ?? 0;
      value.append(rosterQuantity);

      const of = document.createElement("span");
      of.classList.add("ms-1", "me-1");
      of.innerText = "of";
      value.append(of);

      quantityText.innerText = line.quantity;
      value.append(quantityText);

      container.prepend(rosterInfo);

      refreshRosterBtn();
      rosterBtn.classList.add("btn", "btn-primary", "btn-sm", "ms-1");

      const rosterIcon = document.createElement("i");
      rosterIcon.classList.add("bi-list-task");

      rosterBtn.append(rosterIcon);

      rosterBtn.addEventListener("click", () => {
        OffcanvasService.show(
          "Custom Numbers",
          "offcanvas-end offcanvas_small",
        );
      });

      container.append(rosterBtn);
    }

    td.append(container);
    tr.append(td);

    this.tbodyTarget.append(tr);

    this.variantSelectTargets.forEach((vs) =>
      this.updateSelectOptions(vs, vs.value),
    );
  }

  isValidQuantity(line) {
    const rule = this.params.moq_rule;

    return (
      rule &&
      this.isValidQuantityLimit(line, rule) &&
      this.isValidQuantityMultiplicity(line, rule)
    );
  }

  isValidQuantityLimit(line, rule) {
    return (
      line.quantity >= rule.line_min_qty &&
      (rule.line_max_qty == null || line.quantity <= rule.line_max_qty)
    );
  }

  isValidQuantityMultiplicity(line, rule) {
    return (
      rule.line_incr_qty == null || line.quantity % rule.line_incr_qty == 0
    );
  }

  nextThumbnail({ params: { direction } }) {
    const distinctVariants = this.getDistinctSelectedVariants();
    let index = distinctVariants.findIndex(
      (v) => v.thumbnail.url == this.thumbnailTarget.src,
    );
    const length = distinctVariants.length;
    index += direction;
    const newIndex = ((index % length) + length) % length;
    this.thumbnailTarget.src = distinctVariants[newIndex].thumbnail.url;
  }

  showImageModal() {
    const urls = this.params.stock
      ? this.getDistinctSelectedVariants().map((v) => v.thumbnail.url)
      : this.thumbnailTarget.src;
    ImageModalService.show(urls, this.params.product_name);
  }

  disconnect() {
    this.data.set("params", JSON.stringify(this.params));
  }
}
